import { Box, TableCell } from '../primitives';
import * as Icons from '../icons';
import { visuallyHidden } from '../theme/focus';
import { useTranslation } from 'react-i18next';
import { iconSize } from '@munifocus/tokens';

export type SortDir = 'asc' | 'desc';

interface SortableHeaderCellProps {
  label: string;
  sortKey: string;
  activeKey: string;
  dir: SortDir;
  onSort: (key: string) => void;
  align?: 'left' | 'center' | 'right';
  className?: string;
}

/**
 * A sortable table header.
 *
 * The click handler used to sit on the `<th>` itself, which is not focusable
 * and has no key handling — so sorting was unreachable from the keyboard on
 * every table in the portal (WCAG 2.1.1). The control is a real `<button>` now,
 * the cell carries `aria-sort` so the current order is exposed
 * programmatically, and the direction is also stated in text for screen readers
 * rather than being carried by the chevron and its colour alone (1.4.1).
 */
export function SortableHeaderCell({
  label,
  sortKey,
  activeKey,
  dir,
  onSort,
  align = 'left',
  className,
}: SortableHeaderCellProps) {
  const { t } = useTranslation();
  const active = activeKey === sortKey;

  return (
    <TableCell
      // `align` drives the cell's own text alignment too, not just the chevron
      // side — without it a centred/right-aligned body column sits under a
      // left-aligned header.
      align={align}
      className={className}
      aria-sort={active ? (dir === 'asc' ? 'ascending' : 'descending') : 'none'}
      sx={{ whiteSpace: 'nowrap', p: 0 }}
    >
      <Box
        component="button"
        type="button"
        onClick={() => onSort(sortKey)}
        aria-label={t('common.a11y.sortBy', { column: label })}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          flexDirection: align === 'right' ? 'row-reverse' : 'row',
          width: '100%',
          justifyContent:
            align === 'right' ? 'flex-start' : align === 'center' ? 'center' : 'flex-start',
          appearance: 'none',
          border: 'none',
          background: 'none',
          cursor: 'pointer',
          font: 'inherit',
          color: 'inherit',
          userSelect: 'none',
          px: 1.25,
          py: 1.25,
          '&:hover': { color: 'text.primary' },
        }}
      >
        {label}
        <Box
          component="span"
          aria-hidden="true"
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            opacity: active ? 1 : 0.3,
            color: active ? 'primary.main' : 'inherit',
            transition: 'opacity 120ms ease',
          }}
        >
          {active && dir === 'asc' ? (
            <Icons.ChevronUp size={iconSize.sm} />
          ) : (
            <Icons.ChevronDown size={iconSize.sm} />
          )}
        </Box>
        {active && (
          <Box component="span" sx={visuallyHidden}>
            {t(dir === 'asc' ? 'common.a11y.sortedAscending' : 'common.a11y.sortedDescending')}
          </Box>
        )}
      </Box>
    </TableCell>
  );
}
